import { useEffect, useRef, useState } from "react";

const fmtTime = (s) => {
  if (s == null || !isFinite(s) || s < 0) return "0:00";
  const n = Math.floor(s);
  return `${Math.floor(n / 60)}:${String(n % 60).padStart(2, "0")}`;
};

const PlayIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false">
    <path d="M8 5.5v13l11-6.5-11-6.5z" fill="currentColor" />
  </svg>
);

const PauseIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false">
    <rect x="7" y="5.5" width="3.4" height="13" rx="0.6" fill="currentColor" />
    <rect x="13.6" y="5.5" width="3.4" height="13" rx="0.6" fill="currentColor" />
  </svg>
);

export const AudioPlayer = ({ src, duration, label = "voice note", testid }) => {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [metaDur, setMetaDur] = useState(null);

  const stored = typeof duration === "number" && isFinite(duration) && duration > 0 ? duration : null;
  const total = stored || metaDur;
  const ready = total != null && total > 0;

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const onLoaded = () => { if (isFinite(el.duration) && el.duration > 0) setMetaDur(el.duration); };
    const onTime = () => setCurrent(el.currentTime);
    const onEnd = () => { setPlaying(false); setCurrent(0); };
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    el.addEventListener("loadedmetadata", onLoaded);
    el.addEventListener("durationchange", onLoaded);
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("ended", onEnd);
    el.addEventListener("play", onPlay);
    el.addEventListener("pause", onPause);
    return () => {
      el.removeEventListener("loadedmetadata", onLoaded);
      el.removeEventListener("durationchange", onLoaded);
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("ended", onEnd);
      el.removeEventListener("play", onPlay);
      el.removeEventListener("pause", onPause);
    };
  }, [src]);

  const toggle = () => {
    const el = ref.current;
    if (!el) return;
    if (el.paused) el.play().catch(() => {}); else el.pause();
  };

  const seek = (e) => {
    const el = ref.current;
    if (!el) return;
    const t = Number(e.target.value);
    try { el.currentTime = t; } catch { /* metadata not ready */ }
    setCurrent(t);
  };

  return (
    <div className="v142-player" data-testid={testid}>
      <audio ref={ref} src={src} preload="metadata" className="v142-player-audio" />
      <button
        type="button"
        className="v142-player-toggle"
        aria-label={playing ? `Pause ${label}` : `Play ${label}`}
        data-testid={testid ? `${testid}-toggle` : undefined}
        onClick={toggle}
      >
        {playing ? <PauseIcon /> : <PlayIcon />}
      </button>
      {ready ? (
        <>
          <span className="v142-player-time" data-testid={testid ? `${testid}-elapsed` : undefined}>{fmtTime(current)}</span>
          <input
            type="range"
            className="v142-player-seek"
            min={0}
            max={total}
            step={0.1}
            value={Math.min(current, total)}
            onChange={seek}
            aria-label={`Seek ${label}`}
            aria-valuetext={`${fmtTime(current)} of ${fmtTime(total)}`}
          />
          <span className="v142-player-time v142-player-total" data-testid={testid ? `${testid}-total` : undefined}>{fmtTime(total)}</span>
        </>
      ) : (
        <span className="v142-player-loading" aria-live="polite">Loading audio…</span>
      )}
    </div>
  );
};
